import React, { useState, useRef, useEffect } from "react";
import { MdFilterList } from "react-icons/md";

export interface FilterOption {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  label: string;
  options: FilterOption[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = "All",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue === value ? undefined : optionValue);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex w-full min-w-[150px] items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${
          value
            ? "border-biafit-primary bg-biafit-primary bg-opacity-10 text-navy-700 dark:text-white"
            : "border-gray-600 bg-white text-gray-700 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300"
        } focus:border-transparent hover:border-biafit-primary focus:outline-none focus:ring-2 focus:ring-biafit-primary`}
      >
        <div className="flex min-w-0 flex-1 items-center justify-center space-x-2">
          <MdFilterList className="h-4 w-4 flex-shrink-0" />
          <span className="truncate font-medium">{label}</span>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-navy-800">
          <div className="py-1">
            <button
              type="button"
              onClick={() => handleSelect("")}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-navy-700 ${
                !value ? "bg-gray-100 font-medium dark:bg-navy-700" : ""
              } text-gray-700 dark:text-gray-300`}
            >
              {placeholder}
            </button>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-navy-700 ${
                  value === option.value
                    ? "bg-biafit-primary bg-opacity-10 font-medium text-biafit-primary"
                    : "text-gray-700 dark:text-gray-300"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
